import { useMemo } from 'react'
import { useSummariesNeedUpgrade } from '@renderer/chat/hooks/use-plan-features'
import { useAudioNoteSettings } from '@renderer/chat/hooks/useAudioNoteSettings'
import {
  useAudioNoteSummaryError,
  useIsSummaryPending,
  useSavedAudioNoteId,
  useSavedNoteConfirmation,
  useSummaryNoteId,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { useHasStreamingSummaryText, useReadStreamingSummaryText } from '@renderer/hooks/audio/use-streaming-summary'
import { readSummaryGenerationText } from '@renderer/hooks/audio/summary-generation'
import { useRendererOwnsSummaries, useSummaryGenerationView } from '@renderer/hooks/audio/use-summary-generation'
import { SUMMARY_AUTOSTART_WINDOW_MS } from '@shared/audio-note-summary-ownership'
import {
  deriveMeetingSummaryState,
  legacySummaryGeneration,
  type MeetingSummaryInput,
  type MeetingSummaryState,
} from './derive-meeting-summary-state'
import type { MeetingViewModel } from './use-meeting-view-model'

export * from './derive-meeting-summary-state'

type GenerationInput = Pick<MeetingSummaryInput, 'generation' | 'expectsGeneration'>

/** Main's singleton summary state, scoped to this note. */
function useLegacyGeneration(audioNoteId: string | null): GenerationInput {
  const summaryPending = useIsSummaryPending()
  const summaryError = useAudioNoteSummaryError()
  const liveSavedNoteId = useSavedAudioNoteId()
  const summaryNoteId = useSummaryNoteId()
  // Subscribe to "has any text" (a boolean that flips once), and read the text
  // itself without subscribing — every transition that changes the derived
  // phase also changes one of the subscribed inputs, so the snapshot is always
  // read on a render that was going to happen anyway. Subscribing to the text
  // here would re-render the whole summary section on every token.
  const hasStreamingText = useHasStreamingSummaryText()
  const readStreamingSummary = useReadStreamingSummaryText()

  return useMemo(
    () =>
      legacySummaryGeneration({
        audioNoteId,
        liveSavedNoteId,
        summaryNoteId,
        summaryPending,
        streamingSummary: hasStreamingText ? readStreamingSummary() : '',
        summaryError,
      }),
    [audioNoteId, liveSavedNoteId, summaryNoteId, summaryPending, hasStreamingText, readStreamingSummary, summaryError],
  )
}

/** The renderer-owned store's generation for this note; same text-read discipline as above. */
function useRendererGeneration(audioNoteId: string | null): GenerationInput {
  const view = useSummaryGenerationView(audioNoteId)
  const confirmation = useSavedNoteConfirmation()

  return useMemo(() => {
    const generation =
      view && audioNoteId
        ? {
            status: view.status,
            error: view.error,
            unavailable: view.unavailable,
            text: view.hasText ? readSummaryGenerationText(audioNoteId) : '',
          }
        : null
    // Mirrors the auto-start handler's window: a stale confirmation won't start
    // anything, so it mustn't promise a generation either.
    const expectsGeneration =
      audioNoteId !== null &&
      confirmation?.noteId === audioNoteId &&
      Date.now() - confirmation.savedAt <= SUMMARY_AUTOSTART_WINDOW_MS
    return { generation, expectsGeneration }
  }, [view, audioNoteId, confirmation])
}

/** Live summary state for the meeting this view is showing. */
export function useMeetingSummary(model: MeetingViewModel): MeetingSummaryState {
  const rendererOwned = useRendererOwnsSummaries()
  const audioNoteId = model.audioNote?.id ?? null
  const legacy = useLegacyGeneration(audioNoteId)
  const rendererOwnedGeneration = useRendererGeneration(audioNoteId)
  const { isUsingLocalStorage } = useAudioNoteSettings()
  const isFree = useSummariesNeedUpgrade()

  const persistedSummary = model.audioNote?.summary ?? null
  const isLive = model.variant === 'live'
  const { generation, expectsGeneration } = rendererOwned ? rendererOwnedGeneration : legacy

  return useMemo(
    () =>
      deriveMeetingSummaryState({
        isLive,
        audioNoteId,
        persistedSummary,
        generation,
        expectsGeneration,
        isUsingLocalStorage,
        isFree,
      }),
    [isLive, audioNoteId, persistedSummary, generation, expectsGeneration, isUsingLocalStorage, isFree],
  )
}
